import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { toast } from "sonner";
import {
  FileBarChart2, Users, Clock, MinusCircle, PlusCircle, Heart, Palmtree,
  FileText, CalendarRange, Gavel, Download, FileSpreadsheet, FileType2,
  Filter as FilterIcon, Search as SearchIcon,
} from "lucide-react";
import { exportPDF, exportExcel, formatCell, sumColumn, fmtCRC, fmtFecha } from "@/lib/reportesEngine";
import { computePayrollForEmployee, aplicaJornada48 } from "@/lib/payroll";
import { calcularSaldoVacaciones } from "@/lib/vacaciones";
import { weekRange, monthRange } from "@/lib/api";

// Utilidades locales
const iso = (d) => d.toISOString().slice(0, 10);
const startOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const monthLabel = (y, m) => new Date(y, m - 1, 1).toLocaleDateString("es-CR", { month: "long", year: "numeric" });
// Envoltorios que devuelven { inicio: Date-like ISO, fin: Date-like ISO }
const weekRangeFrom = (dateObj) => {
  const day = dateObj.getDay();
  const monday = new Date(dateObj);
  monday.setDate(dateObj.getDate() - ((day + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { inicio: monday, fin: sunday };
};

const REPORTES = [
  { key: "empleados", label: "Empleados", icon: Users, filters: ["depto", "puesto", "estado", "genero"] },
  { key: "asistencia", label: "Asistencia", icon: Clock, filters: ["periodo", "empleadoOpt", "estado"] },
  { key: "rebajos", label: "Rebajos", icon: MinusCircle, filters: ["periodo", "empleadoOpt"] },
  { key: "bonificaciones", label: "Bonificaciones", icon: PlusCircle, filters: ["periodo", "empleadoOpt"] },
  { key: "incapacidades", label: "Incapacidades", icon: Heart, filters: ["periodo", "empleadoOpt", "tipoInc"] },
  { key: "vacaciones", label: "Vacaciones", icon: Palmtree, filters: ["estadoEmp"] },
  { key: "planillas", label: "Planillas semanales", icon: FileText, filters: ["periodoPlanilla"] },
  { key: "planillas_mensuales", label: "Planillas mensuales", icon: CalendarRange, filters: ["mes"] },
  { key: "liquidaciones", label: "Liquidaciones", icon: Gavel, filters: ["periodo", "empleadoOpt"], adminOnly: true },
];

const ESTADOS_ASIST = ["Presente", "Ausente", "Vacaciones", "Incapacidad CCSS", "Incapacidad INS", "Licencia", "Día Libre", "Feriado"];
const TIPOS_INC = ["CCSS", "INS", "Enfermedad", "Otro"];

export default function Reportes() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const REPORTES_VISIBLES = useMemo(() => REPORTES.filter(r => isAdmin || !r.adminOnly), [isAdmin]);
  const [tipo, setTipo] = useState(REPORTES_VISIBLES[0]?.key || "empleados");
  const [empleados, setEmpleados] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [loading, setLoading] = useState(true);

  // filtros comunes
  const [fechaInicio, setFechaInicio] = useState(iso(startOfMonth()));
  const [fechaFin, setFechaFin] = useState(iso(endOfMonth()));
  const [depto, setDepto] = useState("all");
  const [puesto, setPuesto] = useState("all");
  const [estadoEmp, setEstadoEmp] = useState("Activo");
  const [genero, setGenero] = useState("all");
  const [empleadoIds, setEmpleadoIds] = useState([]);
  const [ccssFilter, setCcssFilter] = useState("all"); // all | con | sin
  const [estadoAsist, setEstadoAsist] = useState("all");
  const [tipoInc, setTipoInc] = useState("all");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [semanaStart, setSemanaStart] = useState(iso(weekRangeFrom(new Date()).inicio));
  // Planillas semanales: alternar entre "semanal" y "quincenal" (como en Planillas.js)
  const [tipoPlanilla, setTipoPlanilla] = useState("semanal"); // semanal | quincenal
  const [quincenaHalf, setQuincenaHalf] = useState(0); // 0 = primera (1-15), 1 = segunda (16-fin)

  // data del reporte activo
  const [preview, setPreview] = useState({ columns: [], rows: [] });
  const [generating, setGenerating] = useState(false);
  const [busquedaPreview, setBusquedaPreview] = useState("");

  const currentDef = REPORTES.find(r => r.key === tipo);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [e, emp] = await Promise.all([api.get("/empresa"), api.get("/empleados")]);
        setEmpresa(e.data || {});
        setEmpleados(emp.data || []);
      } catch (err) {
        toast.error("No se pudieron cargar datos base");
      } finally { setLoading(false); }
    })();
  }, []);

  const departamentos = useMemo(() => [...new Set(empleados.map(e => e.departamento).filter(Boolean))].sort(), [empleados]);
  const puestos = useMemo(() => [...new Set(empleados.map(e => e.puesto).filter(Boolean))].sort(), [empleados]);

  // Filtro base de empleados según filtros de reporte
  const empleadosFiltrados = useMemo(() => empleados.filter(e => {
    if (depto !== "all" && (e.departamento || "") !== depto) return false;
    if (puesto !== "all" && (e.puesto || "") !== puesto) return false;
    if (estadoEmp !== "all" && (e.estado || "Activo") !== estadoEmp) return false;
    if (genero !== "all" && (e.genero || "") !== genero) return false;
    if (empleadoIds.length > 0 && !empleadoIds.includes(e.id)) return false;
    if (ccssFilter === "con" && !e.ccss_aplica) return false;
    if (ccssFilter === "sin" && e.ccss_aplica) return false;
    return true;
  }), [empleados, depto, puesto, estadoEmp, genero, empleadoIds, ccssFilter]);

  // ==== GENERADORES POR TIPO DE REPORTE ====
  const generateReport = async () => {
    setGenerating(true);
    try {
      let cols = [], rows = [];
      const filtrosLabel = buildFiltrosLabel();
      const nombreEmpleado = (e) => `${e?.nombre || ""} ${e?.apellidos || ""}`.trim() || "—";
      const empById = Object.fromEntries(empleados.map(e => [e.id, e]));

      switch (tipo) {
        case "empleados": {
          cols = [
            { key: "nombre", label: "Nombre", value: (r) => nombreEmpleado(r) },
            { key: "cedula", label: "Cédula" },
            { key: "departamento", label: "Departamento" },
            { key: "puesto", label: "Puesto" },
            { key: "jornada", label: "Jornada" },
            { key: "genero", label: "Género" },
            { key: "estado", label: "Estado" },
            { key: "fecha_ingreso", label: "Ingreso", type: "date" },
            { key: "salario_base", label: "Salario base", type: "money" },
          ];
          rows = empleadosFiltrados;
          break;
        }
        case "asistencia": {
          const { data } = await api.get(`/asistencia?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
          let list = data || [];
          if (estadoAsist !== "all") list = list.filter(a => (a.estado || "Presente") === estadoAsist);
          const idsFiltro = new Set(empleadosFiltrados.map(e => e.id));
          list = list.filter(a => idsFiltro.has(a.empleado_id));
          cols = [
            { key: "fecha", label: "Fecha", type: "date" },
            { key: "nombre", label: "Trabajador", value: (r) => nombreEmpleado(empById[r.empleado_id]) },
            { key: "estado", label: "Estado" },
            { key: "entrada", label: "Entrada" },
            { key: "salida", label: "Salida" },
            { key: "horas_ordinarias", label: "Hrs ord.", type: "number" },
            { key: "horas_extras", label: "Hrs extra", type: "number" },
          ];
          rows = list.sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
          break;
        }
        case "rebajos": {
          const { data } = await api.get("/rebajos");
          const idsFiltro = new Set(empleadosFiltrados.map(e => e.id));
          const list = (data || []).filter(r => {
            if (!idsFiltro.has(r.empleado_id)) return false;
            const f = String(r.fecha || r.created_at || "").slice(0, 10);
            return f >= fechaInicio && f <= fechaFin;
          });
          cols = [
            { key: "fecha", label: "Fecha", type: "date", value: (r) => r.fecha || r.created_at },
            { key: "nombre", label: "Trabajador", value: (r) => nombreEmpleado(empById[r.empleado_id]) },
            { key: "concepto", label: "Concepto", value: (r) => Array.isArray(r.conceptos) ? r.conceptos.join(", ") : (r.concepto || "—") },
            { key: "descripcion", label: "Descripción" },
            { key: "monto", label: "Monto", type: "money" },
          ];
          rows = list;
          break;
        }
        case "bonificaciones": {
          const { data } = await api.get("/bonificaciones");
          const idsFiltro = new Set(empleadosFiltrados.map(e => e.id));
          const list = (data || []).filter(r => {
            if (!idsFiltro.has(r.empleado_id)) return false;
            const f = String(r.fecha || r.created_at || "").slice(0, 10);
            return f >= fechaInicio && f <= fechaFin;
          });
          cols = [
            { key: "fecha", label: "Fecha", type: "date", value: (r) => r.fecha || r.created_at },
            { key: "nombre", label: "Trabajador", value: (r) => nombreEmpleado(empById[r.empleado_id]) },
            { key: "concepto", label: "Concepto" },
            { key: "descripcion", label: "Descripción" },
            { key: "aplica_ccss", label: "Aplica CCSS", type: "bool" },
            { key: "monto", label: "Monto", type: "money" },
          ];
          rows = list;
          break;
        }
        case "incapacidades": {
          const { data } = await api.get("/incapacidades");
          const idsFiltro = new Set(empleadosFiltrados.map(e => e.id));
          let list = (data || []).filter(r => {
            if (!idsFiltro.has(r.empleado_id)) return false;
            return !(r.fecha_fin < fechaInicio || r.fecha_inicio > fechaFin);
          });
          if (tipoInc !== "all") list = list.filter(r => (r.tipo || "") === tipoInc);
          cols = [
            { key: "nombre", label: "Trabajador", value: (r) => nombreEmpleado(empById[r.empleado_id]) },
            { key: "tipo", label: "Tipo" },
            { key: "fecha_inicio", label: "Desde", type: "date" },
            { key: "fecha_fin", label: "Hasta", type: "date" },
            { key: "dias_total", label: "Días", type: "int" },
            { key: "diagnostico", label: "Diagnóstico" },
            { key: "boleta", label: "N° boleta" },
          ];
          rows = list;
          break;
        }
        case "vacaciones": {
          const { data: asist } = await api.get(`/asistencia?fecha_inicio=1900-01-01&fecha_fin=2100-01-01`);
          const filtroBase = empleadosFiltrados.filter(e => (e.estado || "Activo") === (estadoEmp === "all" ? (e.estado || "Activo") : estadoEmp));
          rows = filtroBase.map(e => {
            const asistEmp = (asist || []).filter(a => a.empleado_id === e.id);
            const saldo = calcularSaldoVacaciones(e, asistEmp, new Date(), empresa);
            return {
              _emp: e,
              nombre: nombreEmpleado(e),
              departamento: e.departamento,
              puesto: e.puesto,
              fecha_ingreso: e.fecha_ingreso,
              periodos: (saldo.periodos || []).length,
              generados: saldo.generados,
              disfrutados: saldo.disfrutados,
              saldo: saldo.saldo,
              estado_venc: saldo.estadoVencimiento,
            };
          });
          cols = [
            { key: "nombre", label: "Trabajador" },
            { key: "departamento", label: "Departamento" },
            { key: "puesto", label: "Puesto" },
            { key: "fecha_ingreso", label: "Ingreso", type: "date" },
            { key: "periodos", label: "Períodos", type: "int" },
            { key: "generados", label: "Días generados", type: "number" },
            { key: "disfrutados", label: "Días disfrutados", type: "number" },
            { key: "saldo", label: "Saldo actual", type: "number" },
            { key: "estado_venc", label: "Estado vencim." },
          ];
          break;
        }
        case "planillas": {
          let inicio, fin;
          if (tipoPlanilla === "semanal") {
            const r = weekRangeFrom(new Date(`${semanaStart}T00:00:00`));
            inicio = r.inicio; fin = r.fin;
          } else {
            // Quincenal — mismo cálculo que Planillas.js
            const y = year, m = month - 1;
            if (quincenaHalf === 0) { inicio = new Date(y, m, 1); fin = new Date(y, m, 15); }
            else { inicio = new Date(y, m, 16); fin = new Date(y, m + 1, 0); }
          }
          const [{ data: asist }, { data: rebajos }, { data: bonos }] = await Promise.all([
            api.get(`/asistencia?fecha_inicio=${iso(inicio)}&fecha_fin=${iso(fin)}`),
            api.get("/rebajos"),
            api.get("/bonificaciones"),
          ]);
          // Incluye TODOS los empleados activos (misma lógica que PlanillasMensuales)
          const empActivos = empleadosFiltrados.filter(e => (e.estado || "Activo") === "Activo");
          const grupoAct = empresa?.actividad_grupo;
          const ccssPensPct = Number(empresa?.ccss_pensionado_pct);
          const ccssPensRate = (isFinite(ccssPensPct) && ccssPensPct > 0) ? ccssPensPct / 100 : null;
          rows = empActivos.map(e => {
            const asEmp = (asist || []).filter(a => a.empleado_id === e.id);
            const reEmp = (rebajos || []).filter(r => r.empleado_id === e.id && r.activo !== false && (r.fecha || r.fecha_inicio) >= iso(inicio) && (r.fecha || r.fecha_inicio) <= iso(fin));
            const boEmp = (bonos || []).filter(b => b.empleado_id === e.id && b.activo !== false && b.fecha >= iso(inicio) && b.fecha <= iso(fin));
            const noHorasExtras = e.puesto && (empresa?.puestos || []).find(p => p.nombre === e.puesto)?.no_horas_extras;
            const freq = String(e.frecuencia_pago || "Mensual").toLowerCase();
            const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
            const ccssRate = ccssPensRate;
            const p = computePayrollForEmployee(e, asEmp, {
              rebajos: reEmp, bonificaciones: boEmp,
              extrasDoblesFeriado: !!empresa.extras_dobles_feriado,
              ccssAplicaExtras: !!empresa.ccss_aplica_extras,
              noHorasExtras, diasBase, ccssRate,
              periodStart: iso(inicio), periodEnd: iso(fin),
              jornada48: aplicaJornada48(empresa, e),
            });
            return {
              nombre: nombreEmpleado(e), puesto: e.puesto,
              horasOrd: p.hrsOrd, horasExt: p.hrsExt,
              bruto: p.totalBruto, ccss: p.ccss, rebajos: p.totalRebajos,
              bonos: p.bonifTotal, neto: p.neto,
            };
          });
          cols = [
            { key: "nombre", label: "Trabajador" },
            { key: "puesto", label: "Puesto" },
            { key: "horasOrd", label: "Hrs ord.", type: "number" },
            { key: "horasExt", label: "Hrs ext.", type: "number" },
            { key: "bruto", label: "Bruto", type: "money" },
            { key: "ccss", label: "CCSS", type: "money" },
            { key: "rebajos", label: "Rebajos", type: "money" },
            { key: "bonos", label: "Bonos", type: "money" },
            { key: "neto", label: "Neto a pagar", type: "money" },
          ];
          break;
        }
        case "planillas_mensuales": {
          const { start, end } = monthRange(year, month - 1);
          const inicio = new Date(`${start}T00:00:00`);
          const fin = new Date(`${end}T00:00:00`);
          const [{ data: asist }, { data: rebajos }, { data: bonos }] = await Promise.all([
            api.get(`/asistencia?fecha_inicio=${iso(inicio)}&fecha_fin=${iso(fin)}`),
            api.get("/rebajos"),
            api.get("/bonificaciones"),
          ]);
          // Incluye TODOS los empleados activos (misma lógica que PlanillasMensuales.js)
          const empActivos = empleadosFiltrados.filter(e => (e.estado || "Activo") === "Activo");
          const grupoAct = empresa?.actividad_grupo;
          const ccssPensPct = Number(empresa?.ccss_pensionado_pct);
          const ccssPensRate = (isFinite(ccssPensPct) && ccssPensPct > 0) ? ccssPensPct / 100 : null;
          rows = empActivos.map(e => {
            const asEmp = (asist || []).filter(a => a.empleado_id === e.id);
            const reEmp = (rebajos || []).filter(r => r.empleado_id === e.id && r.activo !== false && (r.fecha || r.fecha_inicio) >= iso(inicio) && (r.fecha || r.fecha_inicio) <= iso(fin));
            const boEmp = (bonos || []).filter(b => b.empleado_id === e.id && b.activo !== false && b.fecha >= iso(inicio) && b.fecha <= iso(fin));
            const noHorasExtras = e.puesto && (empresa?.puestos || []).find(p => p.nombre === e.puesto)?.no_horas_extras;
            const freq = String(e.frecuencia_pago || "Mensual").toLowerCase();
            const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
            const ccssRate = ccssPensRate;
            const p = computePayrollForEmployee(e, asEmp, {
              rebajos: reEmp, bonificaciones: boEmp,
              extrasDoblesFeriado: !!empresa.extras_dobles_feriado,
              ccssAplicaExtras: !!empresa.ccss_aplica_extras,
              noHorasExtras, diasBase, ccssRate,
              periodStart: iso(inicio), periodEnd: iso(fin),
              jornada48: aplicaJornada48(empresa, e),
            });
            return {
              nombre: nombreEmpleado(e), puesto: e.puesto,
              salario_base: e.salario_base,
              horasExt: p.hrsExt, bruto: p.totalBruto,
              ccss: p.ccss, rebajos: p.totalRebajos, bonos: p.bonifTotal, neto: p.neto,
            };
          });
          cols = [
            { key: "nombre", label: "Trabajador" },
            { key: "puesto", label: "Puesto" },
            { key: "salario_base", label: "Salario base", type: "money" },
            { key: "horasExt", label: "Hrs ext.", type: "number" },
            { key: "bruto", label: "Bruto", type: "money" },
            { key: "ccss", label: "CCSS", type: "money" },
            { key: "rebajos", label: "Rebajos", type: "money" },
            { key: "bonos", label: "Bonos", type: "money" },
            { key: "neto", label: "Neto a pagar", type: "money" },
          ];
          break;
        }
        case "liquidaciones": {
          const { data } = await api.get("/liquidaciones");
          const idsFiltro = new Set(empleadosFiltrados.map(e => e.id));
          const list = (data || []).filter(r => {
            if (!idsFiltro.has(r.empleado_id)) return false;
            const f = String(r.fecha_salida || r.created_at || "").slice(0, 10);
            return f >= fechaInicio && f <= fechaFin;
          });
          cols = [
            { key: "nombre", label: "Trabajador", value: (r) => nombreEmpleado(empById[r.empleado_id]) },
            { key: "fecha_salida", label: "Fecha salida", type: "date" },
            { key: "motivo", label: "Motivo" },
            { key: "aguinaldo", label: "Aguinaldo", type: "money" },
            { key: "vacaciones", label: "Vacaciones", type: "money" },
            { key: "preaviso", label: "Preaviso", type: "money" },
            { key: "cesantia", label: "Cesantía", type: "money" },
            { key: "total", label: "Total liquidación", type: "money" },
          ];
          rows = list;
          break;
        }
        default:
          break;
      }

      setPreview({ columns: cols, rows, filtrosLabel, titulo: currentDef.label });
    } catch (err) {
      console.error(err);
      toast.error("No se pudo generar el reporte: " + (err?.response?.data?.detail || err.message));
    } finally { setGenerating(false); }
  };

  const buildFiltrosLabel = () => {
    const out = [];
    const usesPeriodo = currentDef.filters.includes("periodo");
    if (usesPeriodo) out.push(`Período: ${fmtFecha(fechaInicio)} — ${fmtFecha(fechaFin)}`);
    if (currentDef.filters.includes("periodoPlanilla")) {
      if (tipoPlanilla === "semanal") {
        const { inicio, fin } = weekRangeFrom(new Date(`${semanaStart}T00:00:00`));
        out.push(`Semana: ${fmtFecha(iso(inicio))} — ${fmtFecha(iso(fin))}`);
      } else {
        const y = year, m = month - 1;
        const inicio = quincenaHalf === 0 ? new Date(y, m, 1) : new Date(y, m, 16);
        const fin = quincenaHalf === 0 ? new Date(y, m, 15) : new Date(y, m + 1, 0);
        out.push(`${quincenaHalf === 0 ? "Primera" : "Segunda"} quincena de ${monthLabel(year, month)} (${fmtFecha(iso(inicio))} — ${fmtFecha(iso(fin))})`);
      }
    }
    if (currentDef.filters.includes("mes")) out.push(`Mes: ${monthLabel(year, month)}`);
    if (depto !== "all") out.push(`Departamento: ${depto}`);
    if (puesto !== "all") out.push(`Puesto: ${puesto}`);
    if (estadoEmp !== "all") out.push(`Estado empleado: ${estadoEmp}`);
    if (genero !== "all") out.push(`Género: ${genero}`);
    if (estadoAsist !== "all") out.push(`Estado asist.: ${estadoAsist}`);
    if (tipoInc !== "all") out.push(`Tipo incapacidad: ${tipoInc}`);
    if (empleadoIds.length > 0) out.push(`Trabajadores: ${empleadoIds.length} seleccionado(s)`);
    if (ccssFilter === "con") out.push("Con CCSS");
    else if (ccssFilter === "sin") out.push("Sin CCSS");
    return out;
  };

  const doExport = (fmt) => {
    if (!preview.rows.length) { toast.info("Generá el reporte primero"); return; }
    const payload = {
      titulo: preview.titulo, subtitulo: currentDef.label,
      empresa, filtros: preview.filtrosLabel,
      columns: preview.columns, rows: filteredPreviewRows,
    };
    try {
      if (fmt === "pdf") exportPDF(payload);
      else exportExcel(payload);
      toast.success(`Reporte exportado a ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("Error al exportar: " + e.message);
    }
  };

  const filteredPreviewRows = useMemo(() => {
    if (!busquedaPreview.trim()) return preview.rows;
    const q = busquedaPreview.toLowerCase();
    return preview.rows.filter(r => preview.columns.some(c => {
      const raw = typeof c.value === "function" ? c.value(r) : r[c.key];
      return String(raw ?? "").toLowerCase().includes(q);
    }));
  }, [preview, busquedaPreview]);

  const hasFilter = (k) => currentDef.filters.includes(k);

  return (
    <Layout>
      <header className="mb-6 fade-in-up">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
            <FileBarChart2 size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Módulo</div>
            <h1 className="font-display text-3xl font-semibold">Reportes</h1>
          </div>
        </div>
        <p className="text-sm text-secondary-c">Generá reportes de todos los módulos con filtros dinámicos. Exportá a PDF o Excel.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-5">
        {/* Sidebar: tipo de reporte + filtros */}
        <aside className="space-y-4">
          <div className="np-card p-4">
            <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-3">Tipo de reporte</div>
            <div className="grid grid-cols-1 gap-1" data-testid="reportes-tipo-list">
              {REPORTES_VISIBLES.map(r => {
                const Icon = r.icon;
                const active = tipo === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => { setTipo(r.key); setPreview({ columns: [], rows: [] }); }}
                    data-testid={`reportes-tipo-${r.key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${active ? "bg-primary-c text-white" : "hover:bg-[#F0F0EA] text-primary-c"}`}
                  >
                    <Icon size={16} />
                    <span className="flex-1">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="np-card p-4">
            <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-3 flex items-center gap-2">
              <FilterIcon size={12} /> Filtros
            </div>
            <div className="space-y-3">
              {hasFilter("periodo") && (
                <>
                  <div>
                    <label className="np-label">Desde</label>
                    <input type="date" className="np-input" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} data-testid="rep-fecha-inicio" />
                  </div>
                  <div>
                    <label className="np-label">Hasta</label>
                    <input type="date" className="np-input" value={fechaFin} onChange={e => setFechaFin(e.target.value)} data-testid="rep-fecha-fin" />
                  </div>
                </>
              )}
              {hasFilter("periodoPlanilla") && (
                <>
                  <div>
                    <label className="np-label">Tipo de período</label>
                    <div className="grid grid-cols-2 gap-1 p-1 bg-[#F0F0EA] rounded-lg">
                      <button
                        type="button"
                        onClick={() => setTipoPlanilla("semanal")}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium ${tipoPlanilla === "semanal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`}
                        data-testid="rep-tipo-semanal"
                      >Semanal</button>
                      <button
                        type="button"
                        onClick={() => setTipoPlanilla("quincenal")}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium ${tipoPlanilla === "quincenal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`}
                        data-testid="rep-tipo-quincenal"
                      >Quincenal</button>
                    </div>
                  </div>
                  {tipoPlanilla === "semanal" ? (
                    <div>
                      <label className="np-label">Semana (empieza el lunes)</label>
                      <input type="date" className="np-input" value={semanaStart} onChange={e => setSemanaStart(e.target.value)} data-testid="rep-semana" />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="np-label">Año</label>
                          <select className="np-input" value={year} onChange={e => setYear(Number(e.target.value))} data-testid="rep-quincenal-year">
                            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="np-label">Mes</label>
                          <select className="np-input" value={month} onChange={e => setMonth(Number(e.target.value))} data-testid="rep-quincenal-month">
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleDateString("es-CR", { month: "long" })}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="np-label">Quincena</label>
                        <select className="np-input" value={quincenaHalf} onChange={e => setQuincenaHalf(Number(e.target.value))} data-testid="rep-quincena-half">
                          <option value={0}>Primera quincena (1-15)</option>
                          <option value={1}>Segunda quincena (16-fin)</option>
                        </select>
                      </div>
                    </>
                  )}
                </>
              )}
              {hasFilter("mes") && (
                <>
                  <div>
                    <label className="np-label">Año</label>
                    <select className="np-input" value={year} onChange={e => setYear(Number(e.target.value))} data-testid="rep-year">
                      {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="np-label">Mes</label>
                    <select className="np-input" value={month} onChange={e => setMonth(Number(e.target.value))} data-testid="rep-month">
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                        <option key={m} value={m}>{monthLabel(year, m)}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {hasFilter("depto") && (
                <div>
                  <label className="np-label">Departamento</label>
                  <select className="np-input" value={depto} onChange={e => setDepto(e.target.value)} data-testid="rep-depto">
                    <option value="all">Todos</option>
                    {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}
              {hasFilter("puesto") && (
                <div>
                  <label className="np-label">Puesto</label>
                  <select className="np-input" value={puesto} onChange={e => setPuesto(e.target.value)} data-testid="rep-puesto">
                    <option value="all">Todos</option>
                    {puestos.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              )}
              {(hasFilter("estado") || hasFilter("estadoEmp")) && (
                <div>
                  <label className="np-label">Estado del trabajador</label>
                  <select className="np-input" value={estadoEmp} onChange={e => setEstadoEmp(e.target.value)} data-testid="rep-estado-emp">
                    <option value="all">Todos</option>
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                    <option value="Suspendido">Suspendido</option>
                    <option value="Despedido">Despedido</option>
                  </select>
                </div>
              )}
              {hasFilter("genero") && (
                <div>
                  <label className="np-label">Género</label>
                  <select className="np-input" value={genero} onChange={e => setGenero(e.target.value)} data-testid="rep-genero">
                    <option value="all">Todos</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
              )}
              {/* Filtro CCSS: aplica siempre (todos / con CCSS / sin CCSS) */}
              <div>
                <label className="np-label">CCSS</label>
                <select className="np-input" value={ccssFilter} onChange={e => setCcssFilter(e.target.value)} data-testid="rep-ccss-filter">
                  <option value="all">Todos los trabajadores</option>
                  <option value="con">Con CCSS</option>
                  <option value="sin">Sin CCSS</option>
                </select>
              </div>
              {hasFilter("empleadoOpt") && (
                <div>
                  <label className="np-label">Trabajadores (opcional)</label>
                  <select
                    className="np-input min-h-[120px]"
                    multiple
                    value={empleadoIds}
                    onChange={(e) => setEmpleadoIds(Array.from(e.target.selectedOptions).map(o => o.value))}
                    data-testid="rep-empleados-multi"
                  >
                    {empleados.map(e => (
                      <option key={e.id} value={e.id}>{e.nombre} {e.apellidos || ""}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-secondary-c mt-1">Ctrl/Cmd + click para seleccionar varios. Vacío = todos.</div>
                </div>
              )}
              {hasFilter("estado") && tipo === "asistencia" && (
                <div>
                  <label className="np-label">Estado asistencia</label>
                  <select className="np-input" value={estadoAsist} onChange={e => setEstadoAsist(e.target.value)} data-testid="rep-estado-asist">
                    <option value="all">Todos</option>
                    {ESTADOS_ASIST.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {hasFilter("tipoInc") && (
                <div>
                  <label className="np-label">Tipo de incapacidad</label>
                  <select className="np-input" value={tipoInc} onChange={e => setTipoInc(e.target.value)} data-testid="rep-tipo-inc">
                    <option value="all">Todos</option>
                    {TIPOS_INC.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
            <button
              onClick={generateReport}
              disabled={generating || loading}
              className="np-btn np-btn-primary w-full mt-4"
              data-testid="rep-generate-btn"
            >
              {generating ? "Generando…" : "Generar reporte"}
            </button>
          </div>
        </aside>

        {/* Preview + export */}
        <section className="space-y-4">
          <div className="np-card p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <SearchIcon size={16} className="text-secondary-c" />
              <input
                className="np-input py-1.5"
                placeholder="Buscar en el reporte…"
                value={busquedaPreview}
                onChange={e => setBusquedaPreview(e.target.value)}
                data-testid="rep-preview-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => doExport("pdf")} disabled={!preview.rows.length} className="np-btn np-btn-outline" data-testid="rep-export-pdf">
                <FileType2 size={14} /> PDF
              </button>
              <button onClick={() => doExport("excel")} disabled={!preview.rows.length} className="np-btn np-btn-primary" data-testid="rep-export-excel">
                <FileSpreadsheet size={14} /> Excel
              </button>
            </div>
          </div>

          <div className="np-card p-0 overflow-hidden">
            {!preview.columns.length ? (
              <div className="p-16 text-center" data-testid="rep-empty-state">
                <FileBarChart2 size={40} className="mx-auto text-secondary-c opacity-40 mb-3" />
                <div className="font-medium">Configurá los filtros y presioná Generar reporte</div>
                <div className="text-xs text-secondary-c mt-1">Se mostrarán los resultados acá, y podés exportarlos a PDF o Excel.</div>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-c flex items-center gap-3 flex-wrap">
                  <div className="text-sm">
                    <strong>{filteredPreviewRows.length}</strong> registro{filteredPreviewRows.length === 1 ? "" : "s"}
                  </div>
                  <div className="text-xs text-secondary-c flex-1 truncate">
                    {(preview.filtrosLabel || []).join(" · ")}
                  </div>
                </div>
                <div className="overflow-auto max-h-[65vh]">
                  <table className="w-full text-xs" data-testid="rep-preview-table">
                    <thead className="bg-[#F0F0EA] sticky top-0 z-10">
                      <tr>
                        {preview.columns.map(c => (
                          <th key={c.key} className={`px-3 py-2 font-semibold text-primary-c whitespace-nowrap ${["money", "number", "int"].includes(c.type) ? "text-right" : "text-left"}`}>
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPreviewRows.length === 0 ? (
                        <tr><td colSpan={preview.columns.length} className="p-8 text-center text-secondary-c">Sin resultados con los filtros aplicados.</td></tr>
                      ) : filteredPreviewRows.map((r, i) => (
                        <tr key={r.id || r.empleado_id || `${r.nombre || ""}-${r.fecha || ""}-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                          {preview.columns.map(c => (
                            <td key={c.key} className={`px-3 py-1.5 ${["money", "number", "int"].includes(c.type) ? "text-right font-mono" : ""}`}>
                              {formatCell(r, c)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    {filteredPreviewRows.length > 0 && (
                      <tfoot className="sticky bottom-0 bg-[#50645A] text-white">
                        <tr>
                          {preview.columns.map((c, idx) => {
                            const s = sumColumn(filteredPreviewRows, c);
                            const txt = idx === 0 ? `Total: ${filteredPreviewRows.length}` : s == null ? "" : (c.type === "money" ? fmtCRC(s) : new Intl.NumberFormat("es-CR").format(s));
                            return (
                              <td key={c.key} className={`px-3 py-2 font-semibold ${["money", "number", "int"].includes(c.type) ? "text-right font-mono" : ""}`}>
                                {txt}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
